import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import useMetaTags from '../hooks/useMetaTags';
import { getPost } from '../lib/sanity';
import '../styles/BlogPost.css';

console.log('BlogPost.js file loaded - TEST');

const ptComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?.url) {
        return null;
      }
      return (
        <img
          src={value.asset.url}
          alt={value.alt || ' '}
          className="blog-post-content-image"
          loading="lazy"
        />
      );
    },
    headingWithId: ({ value }) => {
      const HeadingTag = value.level || 'h2';
      return (
        <HeadingTag id={value.id}>
          {value.text}
        </HeadingTag>
      );
    }
  },
  block: {
    h1: ({children, value}) => {
      const anchor = value.markDefs?.find(def => def._type === 'anchor');
      return <h1 id={anchor?.id}>{children}</h1>;
    },
    h2: ({children, value}) => {
      const anchor = value.markDefs?.find(def => def._type === 'anchor');
      return <h2 id={anchor?.id}>{children}</h2>;
    },
    h3: ({children, value}) => {
      const anchor = value.markDefs?.find(def => def._type === 'anchor');
      return <h3 id={anchor?.id}>{children}</h3>;
    },
    h4: ({children, value}) => {
      const anchor = value.markDefs?.find(def => def._type === 'anchor');
      return <h4 id={anchor?.id}>{children}</h4>;
    },
  },
  marks: {
    link: ({children, value}) => {
      const href = value?.href || '';
      // Check if this is an anchor link
      if (href.startsWith('#')) {
        return (
          <a 
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById(href.substring(1));
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {children}
          </a>
        );
      }
      // External link
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Move useMetaTags to the component level
  useMetaTags({
    title: post ? `${post.title} | Sam Penny` : 'Blog Post | Sam Penny',
    description: post?.excerpt || 'Read this blog post by Sam Penny',
  });

  useEffect(() => {
    const loadPost = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fetchedPost = await getPost(slug);
        console.log('Fetched post:', fetchedPost);
        setPost(fetchedPost);
      } catch (err) {
        console.error('Error loading post:', err);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  if (isLoading) {
    return (
      <div className="blog-post-page">
        <div className="skeleton">
          <div className="skeleton-image"></div>
          <div className="skeleton-title"></div>
          <div className="skeleton-text"></div>
          <div className="skeleton-text"></div>
          <div className="skeleton-text"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-page">
        <div className="error-container">
          <h2 className="error-title">Error Loading Post</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post-page">
        <div className="error-container">
          <h2 className="error-title">Post Not Found</h2>
          <p className="error-message">The blog post you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const publishedDate = new Date(post.publishedAt).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="blog-post-page">
      <article className="blog-post-container">
        <div className="blog-post-content-wrapper">
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            Published on {publishedDate}
          </div>
          <div className="blog-post-content">
            <PortableText
              value={post.body}
              components={ptComponents}
            />
          </div>
          
          <div className="author-box">
            <h3 className="author-box-title">About the Author</h3>
            <div className="author-box-content">
              <img src="/images/sam-headshot.webp" alt="Sam Penny" className="author-image" />
              <div className="author-info">
                <h4 className="author-name">Sam Penny</h4>
                <p className="author-bio">
                  Sam Penny is an SEO specialist from Australia. He's interested in the ways that we can use SEO, and search data, to improve the user journey.
                </p>
                <div className="author-social">
                  <a href="https://twitter.com/sampenny_" target="_blank" rel="noopener noreferrer">
                    <img src="/images/x-dark.svg" alt="Twitter" />
                  </a>
                  <a href="https://linkedin.com/in/sampenny" target="_blank" rel="noopener noreferrer">
                    <img src="/images/linkedin-dark.svg" alt="LinkedIn" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost; 